import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Container,
  Form,
  FormGroup,
  FormLabel,
  FormControl,
} from "react-bootstrap";
import BackButton from "../../../../components/UI_Kit/BackButton/BackButton";
import ConfirmationModal from "../../../../components/Modals/ConfirmationModal";
import api from "../../../../services/api";
import "../../styles.css";

const ClientInfo = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    function: "",
    collectivityName: "",
    postalAddress: "",
    siretNumber: "",
  });
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [showBackModal, setShowBackModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams(); // Get the quotation ID from URL params if it exists

  const keyTranslations = {
    firstName: "Prénom",
    lastName: "Nom",
    function: "Fonction",
    collectivityName: "Nom de la collectivité",
    postalAddress: "Adresse postale",
    siretNumber: "Numéro de SIRET",
  };

  // Fetch quotation data if ID is provided
  useEffect(() => {
    const fetchQuotationData = async () => {
      if (!id) return;
      
      try {
        const token = localStorage.getItem("access_token");
        const response = await api.get(`/quotation/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        
        const quotationData = response.data.quotation;
        if (quotationData) {
          setFormData({
            firstName: quotationData.first_name || "",
            lastName: quotationData.last_name || "",
            function: quotationData.job_title || "",
            collectivityName: quotationData.organization_name || "",
            postalAddress: quotationData.postal_address || "",
            siretNumber: quotationData.siret_number || "",
          });
          setIsEditMode(true);
        }
      } catch (error) {
        console.error("Error fetching quotation data:", error);
        toast.error("Impossible de récupérer les informations du devis.");
      }
    };

    fetchQuotationData();
  }, [id]);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  }, []);

  const handleShowModal = useCallback((e) => {
    e.preventDefault();
    setShowModal(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      // Transform formData to match the API field names
      const apiData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        job_title: formData.function,
        organization_name: formData.collectivityName,
        postal_address: formData.postalAddress,
        siret_number: formData.siretNumber,
      };

      const token = localStorage.getItem("access_token");
      let response;
      
      if (isEditMode && id) {
        // Update existing quotation
        response = await api.patch(`/quotations/info-client/${id}`, apiData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Informations client mises à jour avec succès !");
      } else {
        // Create new quotation
        response = await api.post("/quotations/client", apiData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        toast.success("Formulaire soumis avec succès !");
      }

      setShowModal(false);
      
      setTimeout(() => {
        // Use the ID from the response for new quotations, or the existing ID for updates
        const quotationId = isEditMode ? id : response.data.data.id;
        navigate(`/invoice/create/step2/${quotationId}`);
      }, 1000);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isEditMode, id, navigate]);

  const formFields = useMemo(
    () => [
      {
        label: "Prénom",
        name: "firstName",
        placeholder: "Prénom",
        required: true,
      },
      { label: "Nom", name: "lastName", placeholder: "Nom", required: true },
      {
        label: "Fonction",
        name: "function",
        placeholder: "Ex: Adjoint au Maire, chargé de prévention...",
        required: true,
      },
      {
        label: "Nom de la collectivité",
        name: "collectivityName",
        placeholder: "Nom de la collectivité",
        required: true,
      },
      {
        label: "Adresse postale",
        name: "postalAddress",
        placeholder: "N° de rue, Code postal, Ville",
        required: true,
      },
      {
        label: "Numéro de SIRET",
        name: "siretNumber",
        placeholder: "XXX XXX XXX XXXXX",
        required: false,
      },
    ],
    []
  );

  useEffect(() => {
    // Vérifie si tous les champs obligatoires sont remplis
    const requiredFields = formFields
      .filter(field => field.required)
      .map(field => field.name);
    
    const allRequiredFieldsFilled = requiredFields.every(
      fieldName => formData[fieldName].trim() !== ""
    );
    
    setIsFormDirty(allRequiredFieldsFilled);
  }, [formData, formFields]);

  const handleBackClick = useCallback(() => {
    if (isFormDirty) {
      setShowBackModal(true);
    } else {
      navigate("/"); // Revenir directement en arrière si le formulaire est vide
    }
  }, [isFormDirty, navigate]);

  return (
    <>
      <ToastContainer />
      <Helmet>
        <title>{isEditMode ? "Modifier" : "Créer"} un devis</title>
        <meta name="description" content="Formulaire de création de devis" />
      </Helmet>
      <div className="container-wrap-custom " />
      <Container className="container-wrap pt-4 d-flex justify-content-center ">
        <div className=" w-100">
          <div className="header ">
            <BackButton className="back-button" onClick={handleBackClick} />
            <span className="step-title">{isEditMode ? "Modifier" : "Créer"} un devis</span>
          </div>

          <span className=" step_bar d-flex justify-content-between align-items-center w-100">
            <img
              className="step-image img-fluid"
              src="/step_done.png"
              alt="step1"
            />
            <img className="step-image img-fluid" src="/step.png" alt="step2" />
            <img className="step-image img-fluid" src="/step.png" alt="step3" />
          </span>

          <p className="form-title">Informations client</p>

          <Form onSubmit={handleShowModal}>
            {formFields.map(({ label, name, placeholder, required }) => (
              <FormGroup className="mb-3" key={name}>
                <FormLabel>
                  {label}
                  {required && "*"}
                </FormLabel>
                <FormControl
                  type="text"
                  name={name}
                  placeholder={placeholder}
                  value={formData[name]}
                  onChange={handleChange}
                  required={required}
                />
              </FormGroup>
            ))}

            <p className="p-2 text-muted">*Champs obligatoires</p>

            <div className="footer shadow-lg d-flex justify-content-center">
              <button
                className="w-100 primary-button primary-button-block"
                type="submit"
                disabled={!isFormDirty} // Disable button if form is not dirty
              >
                Suivant
              </button>
            </div>
          </Form>

          <ConfirmationModal
            show={showModal}
            handleClose={() => setShowModal(false)}
            handleConfirm={handleSubmit}
            title="Vérifiez les informations avant de continuer"
            isSubmitting={isSubmitting}
            buttonTitle="Passer à l'étape suivante"
            LinkTitle2="Revenir aux informations"
          >
            {Object.entries(formData).map(([key, value]) => (
              <span key={key} className="mt-2">
                <span>{keyTranslations[key] || key} </span>
                <p>{value || "Non renseigné"}</p>
              </span>
            ))}
          </ConfirmationModal>

          <ConfirmationModal
            show={showBackModal}
            handleClose={() => setShowBackModal(false)}
            handleConfirm={() => navigate("/")} // Confirmer et revenir en arrière
            title="Vous n'avez pas terminé de compléter votre devis"
            isSubmitting={false}
            buttonTitle="Enregistrer comme brouillon"
            LinkTitle="Quitter sans enregistrer"
          >
            <p className="text-center">
              Vous êtes sur le point de quitter sans avoir terminé votre devis.
              Êtes-vous sûr de vouloir quitter ? <br/>
              Sinon, vous pouvez l'enregistrer en brouillon pour le retrouver plus tard.
            </p>
          </ConfirmationModal>
        </div>
      </Container>
    </>
  );
};

export default ClientInfo;
